import React from 'react';
import {
  Container, Box, Typography, Grid, Card, CardContent, Paper,
  Button, Chip, List, ListItem, ListItemIcon, ListItemText, Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Wifi, Devices, Security, Lock, Router, SettingsRemote,
  Backup, CheckCircle, Home, Shield, Visibility
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigateAndScroll } from '../utils/navigation';
import photos from '../data/photos';
import SEO from '../components/common/SEO';

const HeroSection = styled(Box)({
  backgroundImage: `linear-gradient(rgba(30,58,138,0.82), rgba(124,58,237,0.82)), url("${photos.heroes.homeAssessment}")`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: 'white',
  padding: '80px 0',
  textAlign: 'center',
});

const CategoryCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderTop: '4px solid',
  borderImage: 'linear-gradient(135deg, #2563eb, #7c3aed) 1',
}));

function HomeAssessmentPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const assessmentCategories = [
    {
      photo: photos.homeAssessment.networkSecurity,
      title: 'Network Security',
      points: '35 points',
      items: [
        { check: 'Router admin credentials audit', desc: 'We verify your router isn\'t using default admin passwords that anyone can Google. You\'d be surprised how many routers are wide open.', points: 10 },
        { check: 'WiFi encryption & password strength', desc: 'We evaluate whether your WiFi uses current encryption (WPA3 or WPA2) and whether your password is strong enough to resist cracking tools.', points: 15 },
        { check: 'Guest network isolation', desc: 'If you have a guest network, we verify it\'s properly isolated from your main network so visitors can\'t access your devices or files.', points: 10 },
      ]
    },
    {
      photo: photos.homeAssessment.deviceSecurity,
      title: 'Device Security',
      points: '45 points',
      items: [
        { check: 'Antivirus & anti-malware status', desc: 'We check every computer and mobile device for active, up-to-date endpoint protection. We verify real-time scanning is enabled and definitions are current.', points: 15 },
        { check: 'Operating system & software updates', desc: 'Outdated software is one of the biggest attack vectors. We check every device for pending OS updates, browser patches, and application updates.', points: 10 },
        { check: 'IoT & smart home device audit', desc: 'This is the big one. We inventory every connected device (smart speakers, cameras, doorbells, thermostats, baby monitors, smart TVs, robot vacuums) and check each for default passwords, firmware updates, and proper network isolation.', points: 20 },
      ]
    },
    {
      photo: photos.homeAssessment.dataSecurity,
      title: 'Data Security',
      points: '20 points',
      items: [
        { check: 'Backup strategy evaluation', desc: 'Do you have backups? Are they automatic? Are they stored off-site or in the cloud? Could you recover from a ransomware attack? We evaluate your entire backup strategy.', points: 10 },
        { check: 'File sharing & cloud storage review', desc: 'We check your cloud storage settings (Google Drive, Dropbox, iCloud, OneDrive) for accidentally shared folders, public links, and overly permissive access.', points: 10 },
      ]
    },
    {
      photo: photos.homeAssessment.userAwareness,
      title: 'User Awareness & Habits',
      points: '20 points',
      items: [
        { check: 'Password practices assessment', desc: 'Are you reusing passwords? Using a password manager? Have your credentials appeared in known data breaches? We evaluate your password hygiene across all household accounts.', points: 10 },
        { check: 'Phishing and social engineering awareness', desc: 'Can your household members spot a phishing email or a fake login page? We review real examples with you and assess how vulnerable your family is to the most common attack vector on the internet.', points: 5 },
        { check: 'Multi-factor authentication review', desc: 'We check whether MFA is enabled on your critical accounts: email, banking, social media, and cloud storage. If it is not, we walk you through setting it up before we leave.', points: 5 },
      ]
    },
  ];

  return (
    <Box>
      <SEO title="Home Assessment" description="Home cybersecurity assessment for $249.99. Network, device, IoT audit by supervised college interns." />
      {/* Hero */}
      <HeroSection>
        <Container maxWidth="lg">
          <Chip icon={<Home sx={{ color: 'white !important' }} />} label="For Homeowners"
            sx={{ mb: 3, backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }} />
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Home Cybersecurity Assessment
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.95, maxWidth: 750, mx: 'auto', mb: 4 }}>
            The average home now has 15-20 connected devices. Our experts audit every one of them (routers, computers, phones, smart home devices, IoT gadgets) and give you a clear plan to lock it all down.
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigateAndScroll(navigate, '/book', location.pathname)}
            sx={{ backgroundColor: '#fbbf24', color: '#1e1e1e', boxShadow: '0 4px 20px rgba(0,0,0,0.4)', border: '2px solid #fbbf24', px: 5, py: 1.5, fontSize: '1.2rem', fontWeight: 700 }}>
            Book Home Assessment: $249.99
          </Button>
        </Container>
      </HeroSection>

      {/* What You Get */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
          What We Do For You
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 6, fontSize: '1.1rem', lineHeight: 1.8, maxWidth: 700, mx: 'auto' }}>
          A CWC intern arrives at your home, spends one focused hour evaluating your entire digital security setup, walks you through what they found, and then a CISSP-certified expert reviews everything before you get your report. Here's exactly what we assess:
        </Typography>
      </Container>

      {/* Assessment Categories - Detailed */}
      <Box sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          {assessmentCategories.map((cat, i) => (
            <Paper key={i} elevation={1} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
              <Box sx={{ width: 380, height: 180, overflow: 'hidden', borderRadius: 2, mb: 3 }}>
                <Box component="img" src={cat.photo} alt={cat.title}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>{cat.title}</Typography>
              <Grid container spacing={3}>
                {cat.items.map((item, j) => (
                  <Grid item xs={12} md={cat.items.length === 2 ? 6 : 4} key={j}>
                    <Card variant="outlined" sx={{ height: '100%', borderColor: 'grey.200' }}>
                      <CardContent>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.check}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{item.desc}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ))}
        </Container>
      </Box>

      {/* What Happens After */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
          What You Receive
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {[
            { title: '1-Hour On-Site Assessment', desc: 'A vetted intern systematically evaluates every aspect of your home\'s digital security using our comprehensive scoring framework.' },
            { title: '15-Minute Consultation', desc: 'Your intern walks you through their initial findings on the spot: what\'s good, what needs attention, and what\'s urgent.' },
            { title: 'Expert-Reviewed Report', desc: 'Within 48 hours, a CISSP-certified professional reviews all findings and delivers your comprehensive security report.' },
            { title: 'Prioritized Action Plan', desc: 'Not just a list of problems. This is a ranked roadmap telling you exactly what to fix first, second, third, with clear instructions.' },
            { title: 'CWC Security Certificate', desc: 'A dated certificate documenting your assessment, useful for insurance purposes and peace of mind.' },
          ].map((item, i) => (
            <Grid item xs={12} key={i}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <CheckCircle sx={{ color: 'success.main', mt: 0.5, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{item.title}</Typography>
                  <Typography variant="body1" color="text.secondary">{item.desc}</Typography>
                </Box>
              </Box>
              {i < 4 && <Divider sx={{ mt: 2 }} />}
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Scope: Included vs Not Included */}
      <Box sx={{ backgroundColor: 'grey.50', py: 6 }}>
        <Container maxWidth="md">
          <Typography variant="h4" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
            Assessment Scope
          </Typography>
          <Grid container spacing={4} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#16a34a', mb: 2 }}>Included</Typography>
              {[
                'WiFi and router security configuration review',
                'Password and account hygiene check',
                'Device inventory and risk review (all connected devices)',
                'Smart home and IoT security audit',
                'Software update and patch status review',
                'Backup strategy evaluation',
                'Cloud storage and file sharing audit',
                'Phishing awareness review',
                'MFA (multi-factor authentication) check',
                '15-minute personalized consultation',
                'CISSP expert-reviewed report within 48 hours',
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <CheckCircle sx={{ color: '#16a34a', fontSize: 18, mt: 0.3 }} />
                  <Typography variant="body2">{item}</Typography>
                </Box>
              ))}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#dc2626', mb: 2 }}>Not Included</Typography>
              {[
                'Penetration testing or active exploitation',
                'Deep forensic analysis of devices',
                'Enterprise network architecture design',
                'Ongoing monitoring or managed security',
                'Malware removal or incident response',
                'Hardware installation or configuration changes',
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>&#x2717; {item}</Typography>
                </Box>
              ))}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                Need something beyond a standard assessment? Contact us to discuss custom engagements.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Common Findings */}
      <Box sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
            What We Commonly Find
          </Typography>
          <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 4 }}>
            These are real issues we discover in a majority of home assessments:
          </Typography>
          {[
            'Routers still using factory-default admin credentials (admin/admin)',
            'Smart home devices (cameras, doorbells, baby monitors) with default passwords and outdated firmware',
            'Computers running with Windows Defender disabled or antivirus definitions months out of date',
            'WiFi networks using WPA2 with weak, short passwords vulnerable to dictionary attacks',
            'Cloud storage folders accidentally shared with "anyone with the link"',
            'No backup strategy, just one ransomware attack away from losing everything',
            'Old devices still connected to the network that haven\'t been updated in years',
            'Password reuse across multiple accounts, with credentials already in known breach databases',
          ].map((finding, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'flex-start' }}>
              <Visibility sx={{ color: '#dc2626', mt: 0.3, flexShrink: 0, fontSize: 20 }} />
              <Typography variant="body1">{finding}</Typography>
            </Box>
          ))}
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ backgroundImage: `linear-gradient(rgba(30,58,138,0.85), rgba(124,58,237,0.85)), url("${photos.heroes.homeAssessment}")`,
        backgroundSize: 'cover', backgroundPosition: 'center', py: 8 }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" color="white" gutterBottom sx={{ fontWeight: 700 }}>
            Assess Your Home for $249.99
          </Typography>
          <Typography variant="h6" color="white" sx={{ mb: 4, opacity: 0.9 }}>
            One hour. One flat price. One report that tells you exactly what to fix.
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigateAndScroll(navigate, '/book', location.pathname)}
            sx={{ backgroundColor: '#fbbf24', color: '#1e1e1e', boxShadow: '0 4px 20px rgba(0,0,0,0.4)', border: '2px solid #fbbf24', px: 5, py: 1.5, fontSize: '1.2rem', fontWeight: 700 }}>
            Book My Home Assessment
          </Button>
        </Container>
      </Box>
    </Box>
  );
}

export default HomeAssessmentPage;
